import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { db as firestoreDb, fn } from 'firebase-modules';

import {
  OrganizationRole,
  FirestoreOrganizationMember,
  SessionUserPermission,
} from '@creative-kit/shared';
import type { RootStore } from '.';
import { trackAction } from './analytics';
import { isPartialMemberRemovalError, showMemberRemovalRetrySnackbar } from './memberRemovalRetry';

export class UiOrganizationMemberStore {
  private rootStore: RootStore;

  members: FirestoreOrganizationMember[] = [];
  organizationMembers: Record<string, FirestoreOrganizationMember[]> = {};
  isloading = false;
  isInviting = false;
  isRemoving = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      members: observable,
      organizationMembers: observable,
      isloading: observable,
      isInviting: observable,
      isRemoving: observable,
      error: observable,
      loadMembers: action.bound,
      inviteMember: action.bound,
      removeMember: action.bound,
      changeMemberRole: action.bound,
      transferOwnership: action.bound,
      currentOrganizationMembers: computed,
    });

    this.rootStore = rootStore;
  }

  async loadMembers(organizationId: string) {
    this.isloading = true;
    this.error = null;

    try {
      const snapshot = await firestoreDb
        .collection('organizations')
        .doc(organizationId)
        .collection('members')
        .get();

      const members: FirestoreOrganizationMember[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as FirestoreOrganizationMember;
        members.push({ ...data, userId: doc.id });
      });

      runInAction(() => {
        this.members = members;
        this.organizationMembers[organizationId] = members;
        this.isloading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load members';
        this.isloading = false;
      });
    }
  }

  async inviteMember(
    organizationId: string,
    email: string,
    role: OrganizationRole,
    name = '',
    message = '',
  ) {
    if (this.isInviting) return; // Prevent double-clicks

    this.isInviting = true;
    this.error = null;

    try {
      const inviteMemberFn = fn.httpsCallable('inviteMember');
      const result = await inviteMemberFn({
        organizationId,
        email,
        role,
        name,
        message,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message,
      });

      // Reload members
      await this.loadMembers(organizationId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to invite member';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
    } finally {
      runInAction(() => {
        this.isInviting = false;
      });
    }
  }

  async removeMember(organizationId: string, memberId: string) {
    if (this.isRemoving) return; // Prevent double-clicks

    this.isRemoving = true;
    this.error = null;

    try {
      const removeMemberFn = fn.httpsCallable('removeMember');
      const result = await removeMemberFn({
        organizationId,
        memberId,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message,
      });

      trackAction('member_removed', 'modal');

      // Reload members
      await this.loadMembers(organizationId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';

      if (isPartialMemberRemovalError(err)) {
        showMemberRemovalRetrySnackbar(this.rootStore, errorMessage, () =>
          this.removeMember(organizationId, memberId),
        );
      } else {
        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: errorMessage,
        });
      }
      throw err;
    } finally {
      runInAction(() => {
        this.isRemoving = false;
      });
    }
  }

  async changeMemberRole(organizationId: string, memberId: string, newRole: OrganizationRole) {
    try {
      const changeMemberRoleFn = fn.httpsCallable('changeMemberRole');
      const result = await changeMemberRoleFn({
        organizationId,
        memberId,
        newRole,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message,
      });

      // Reload members
      await this.loadMembers(organizationId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change role';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
    }
  }

  async transferOwnership(organizationId: string, newOwnerId: string) {
    try {
      const transferOwnershipFn = fn.httpsCallable('transferOwnership');
      const result = await transferOwnershipFn({
        organizationId,
        newOwnerId,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message,
      });

      // Reload members
      await this.loadMembers(organizationId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to transfer ownership';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
    }
  }

  get currentOrganizationMembers(): FirestoreOrganizationMember[] {
    if (!this.rootStore.authStore.user?.uid) return [];

    // For now, we'll need to find the organization the user belongs to
    // This could be enhanced to handle multi-organization users
    return this.members;
  }

  /**
   * Extract domain from email address
   */
  private static extractDomain(email: string): string {
    return email.split('@')[1]?.toLowerCase() || '';
  }

  /**
   * Get all unique domains from organization members
   */
  getOrganizationDomains(organizationId: string): string[] {
    const members = this.organizationMembers[organizationId] || [];
    const domains = new Set<string>();

    members.forEach((member) => {
      if (member.email) {
        const domain = UiOrganizationMemberStore.extractDomain(member.email);
        if (domain) {
          domains.add(domain);
        }
      }
    });

    return Array.from(domains);
  }

  /**
   * Find members with matching domain
   */
  getMembersByDomain(organizationId: string, domain: string): FirestoreOrganizationMember[] {
    const members = this.organizationMembers[organizationId] || [];
    return members.filter((member) => {
      if (!member.email) return false;
      return UiOrganizationMemberStore.extractDomain(member.email) === domain.toLowerCase();
    });
  }

  /**
   * Get domain groups for the organization
   */
  getDomainGroups(organizationId: string): Record<string, FirestoreOrganizationMember[]> {
    const members = this.organizationMembers[organizationId] || [];
    const domainGroups: Record<string, FirestoreOrganizationMember[]> = {};

    members.forEach((member) => {
      if (member.email) {
        const domain = UiOrganizationMemberStore.extractDomain(member.email);
        if (domain) {
          if (!domainGroups[domain]) {
            domainGroups[domain] = [];
          }
          domainGroups[domain].push(member);
        }
      }
    });

    return domainGroups;
  }

  /**
   * Check if domain-based access should be enabled for this organization
   */
  shouldEnableDomainAccess(organizationId: string, maxOrgSize: number = 100): boolean {
    const members = this.organizationMembers[organizationId] || [];
    return members.length <= maxOrgSize;
  }

  /**
   * Get recommended domain access settings based on organization composition
   */
  getRecommendedDomainSettings(organizationId: string): {
    enabled: boolean;
    maxOrgSize: number;
    defaultPermission: SessionUserPermission;
  } {
    const members = this.organizationMembers[organizationId] || [];
    const domainGroups = this.getDomainGroups(organizationId);
    const domainCount = Object.keys(domainGroups).length;

    // If most members share domains, enable domain access
    const sharedDomainMembers = Object.values(domainGroups)
      .filter((group) => group.length > 1)
      .reduce((total, group) => total + group.length, 0);

    const shouldEnable = domainCount > 1 && sharedDomainMembers > members.length * 0.5;

    return {
      enabled: shouldEnable,
      maxOrgSize: Math.min(100, members.length * 2), // Reasonable limit
      defaultPermission: SessionUserPermission.VIEW,
    };
  }
}
